import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../services/PermissionsContext';

const API_MY_PAYABLE_PERMS = '/api/admin/payable-user-permissions/my-permissions/';
const API_PAYABLE_NOTIFICATION_DATA = '/api/admin/payable-user-permissions/notification-data/';
const STAGES = [1, 2, 3];

const STAGE_COLORS = {
  1: { bg: '#e3f2fd', border: '#64b5f6', text: '#0d47a1' },
  2: { bg: '#b3e5fc', border: '#42a5f5', text: '#0d47a1' },
  3: { bg: '#81d4fa', border: '#1e88e5', text: '#0d47a1' },
};

const CASH_STAGE_COLORS = {
  1: { bg: '#e8f5e9', border: '#81c784', text: '#1b5e20' },
  2: { bg: '#c8e6c9', border: '#66bb6a', text: '#1b5e20' },
  3: { bg: '#a5d6a7', border: '#43a047', text: '#1b5e20' },
};

const authHeaders = (json = true) => {
  const token = localStorage.getItem('kk_token') || localStorage.getItem('token');
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

const PayablesNotification = () => {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const isSuperAdmin = !!permissions?.is_superuser;

  const [myPerms, setMyPerms] = useState([]);
  const [data, setData] = useState(null);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const timerRef = useRef(null);
  const isMountedRef = useRef(false);
  const hasScheduledRef = useRef(false);
  const originalTitleRef = useRef(null);
  const flashTitleRef = useRef(null);

  const stopFlashTitle = useCallback(() => {
    if (flashTitleRef.current) {
      clearInterval(flashTitleRef.current);
      flashTitleRef.current = null;
    }
    if (originalTitleRef.current !== null) {
      document.title = originalTitleRef.current;
      originalTitleRef.current = null;
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [permRes, dataRes] = await Promise.all([
        fetch(API_MY_PAYABLE_PERMS, { headers: authHeaders(false) }),
        fetch(API_PAYABLE_NOTIFICATION_DATA, { headers: authHeaders(false) })
      ]);
      if (!permRes.ok || !dataRes.ok) return;
      const permData = await permRes.json();
      const notificationData = await dataRes.json();
      setMyPerms(permData);
      setData(notificationData);
    } catch (e) {
      // ignore
    }
  }, []);

  const scheduleNext = useCallback((intervalMinutes) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const ms = Math.max(intervalMinutes * 60 * 1000, 60000);
    timerRef.current = setTimeout(async () => {
      await fetchData();
      if (isMountedRef.current) {
        setShow(true);
      }
    }, ms);
  }, [fetchData]);

  const resetTimer = useCallback(() => {
    setShow(false);
    if (data) {
      scheduleNext(data.interval_minutes);
    }
  }, [data, scheduleNext]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!data || show || hasScheduledRef.current) return;
    hasScheduledRef.current = true;
    scheduleNext(data.interval_minutes);
  }, [data, show, scheduleNext]);

  useEffect(() => {
    if (!show) {
      stopFlashTitle();
      return;
    }
    // Only flash if the notification appears while the user is on another tab
    if (!document.hidden && document.hasFocus()) return;

    originalTitleRef.current = document.title;
    const alertTitle = '🔴 New Payable Notification';
    let on = true;
    flashTitleRef.current = setInterval(() => {
      document.title = on ? alertTitle : originalTitleRef.current;
      on = !on;
    }, 1000);

    const handleFocus = () => stopFlashTitle();
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleFocus);
    };
  }, [show, stopFlashTitle]);

  const canViewPayableStage = (stage) => {
    if (isSuperAdmin) return true;
    return (myPerms || []).some(p => p.stage === stage && p.can_approve_payables);
  };

  const canViewCashStage = (stage) => {
    if (isSuperAdmin) return true;
    return (myPerms || []).some(p => p.stage === stage && p.can_approve_cash);
  };

  const canViewPayableReceipts = () => {
    if (isSuperAdmin) return true;
    return (myPerms || []).some(p => p.can_view_payables || p.can_approve_payables);
  };

  const canViewCashReceipts = () => {
    if (isSuperAdmin) return true;
    return (myPerms || []).some(p => p.can_view_cash || p.can_approve_cash);
  };

  const stageTotals = data ? data.stages || {} : {};
  const cashStageTotals = data ? data.cash_stages || {} : {};

  const pendingReceipts = data ? (data.pending_receipts || 0) : 0;
  const uploadedReceipts = data ? (data.uploaded_receipts || 0) : 0;
  const cashPendingReceipts = data ? (data.cash_pending_receipts || 0) : 0;
  const cashUploadedReceipts = data ? (data.cash_uploaded_receipts || 0) : 0;

  const formatCount = (count) => (count === undefined || count === null ? 0 : count);

  const visiblePayableStages = data ? STAGES.filter(stage => canViewPayableStage(stage) && (stageTotals[stage] || 0) > 0) : [];
  const visibleCashStages = data ? STAGES.filter(stage => canViewCashStage(stage) && (cashStageTotals[stage] || 0) > 0) : [];

  const showPayableReceipts = canViewPayableReceipts() && (pendingReceipts > 0 || uploadedReceipts > 0);
  const showCashReceipts = canViewCashReceipts() && (cashPendingReceipts > 0 || cashUploadedReceipts > 0);

  const hasVisiblePayables = visiblePayableStages.length > 0 || showPayableReceipts;
  const hasVisibleCash = visibleCashStages.length > 0 || showCashReceipts;

  const handleStageClick = (stage, isCash) => {
    resetTimer();
    const target = isCash ? '/dashboard/CashOfficeApprovals' : '/dashboard/PayablesApprovals';
    navigate(target, { state: { defaultStage: stage } });
  };

  const handleClose = () => {
    resetTimer();
  };

  useEffect(() => {
    if (show && !hasVisiblePayables && !hasVisibleCash) {
      resetTimer();
    }
  }, [show, hasVisiblePayables, hasVisibleCash, resetTimer]);

  useEffect(() => {
    const showNotification = () => {
      fetchData().then(() => setShow(true));
    };
    window.addEventListener('show-payables-notification', showNotification);
    return () => window.removeEventListener('show-payables-notification', showNotification);
  }, [fetchData]);

  if (!hasVisiblePayables && !hasVisibleCash) return null;

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      backgroundColor: 'rgba(0,0,0,0.55)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20
    }}>
      <div style={{
        backgroundColor: '#fff',
        borderRadius: 8,
        width: '100%',
        maxWidth: 640,
        boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
        overflow: 'hidden'
      }}>
        <div style={{
          backgroundColor: '#3f6b32',
          color: '#fff',
          padding: '14px 18px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>Payables Pending Approvals</h3>
          <button onClick={handleClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ padding: 24 }}>
          {visiblePayableStages.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <h4 style={{ margin: '0 0 14px 0', fontSize: 15, color: '#333' }}>Payables Pending Approvals:</h4>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {visiblePayableStages.map(stage => (
                  <button
                    key={stage}
                    onClick={() => handleStageClick(stage, false)}
                    style={{
                      flex: '1 1 100px',
                      padding: '14px 10px',
                      border: '1px solid ' + STAGE_COLORS[stage].border,
                      borderRadius: 6,
                      background: STAGE_COLORS[stage].bg,
                      cursor: 'pointer',
                      textAlign: 'center'
                    }}
                  >
                    <div style={{ fontSize: 15, fontWeight: 'bold', color: stage === 3 ? 'red' : '#0d47a1', marginBottom: 6 }}>Stage {stage}</div>
                    <div style={{ borderTop: '1px solid #fff', width: '100%', margin: '6px 0' }} />
                    <div style={{ fontSize: 20, fontWeight: 'bold', color: '#0d47a1' }}>{formatCount(stageTotals[stage])}</div>
                  </button>
                ))}
              </div>

              {showPayableReceipts && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {pendingReceipts > 0 && (
                      <div onClick={() => handleStageClick(6, false)} style={{ flex: '1 1 100px', padding: '12px 10px', border: '1px solid #64b5f6', borderRadius: 6, background: '#e3f2fd', cursor: 'pointer', textAlign: 'center' }}>
                        <div style={{ fontSize: 14, fontWeight: 'bold', color: '#0d47a1', marginBottom: 4 }}>Pending Receipts</div>
                        <div style={{ fontSize: 18, fontWeight: 'bold', color: '#0d47a1' }}>{formatCount(pendingReceipts)}</div>
                      </div>
                    )}
                    {uploadedReceipts > 0 && (
                      <div onClick={() => handleStageClick(7, false)} style={{ flex: '1 1 100px', padding: '12px 10px', border: '1px solid #64b5f6', borderRadius: 6, background: '#e3f2fd', cursor: 'pointer', textAlign: 'center' }}>
                        <div style={{ fontSize: 14, fontWeight: 'bold', color: '#0d47a1', marginBottom: 4 }}>Uploaded Receipts</div>
                        <div style={{ fontSize: 18, fontWeight: 'bold', color: '#0d47a1' }}>{formatCount(uploadedReceipts)}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {visibleCashStages.length > 0 && (
            <div>
              <h4 style={{ margin: '0 0 14px 0', fontSize: 15, color: '#333' }}>Cash Payables Pending Approvals:</h4>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {visibleCashStages.map(stage => (
                  <button
                    key={stage}
                    onClick={() => handleStageClick(stage, true)}
                    style={{
                      flex: '1 1 100px',
                      padding: '14px 10px',
                      border: '1px solid ' + CASH_STAGE_COLORS[stage].border,
                      borderRadius: 6,
                      background: CASH_STAGE_COLORS[stage].bg,
                      cursor: 'pointer',
                      textAlign: 'center'
                    }}
                  >
                    <div style={{ fontSize: 15, fontWeight: 'bold', color: stage === 3 ? 'red' : '#1b5e20', marginBottom: 6 }}>Stage {stage}</div>
                    <div style={{ borderTop: '1px solid #fff', width: '100%', margin: '6px 0' }} />
                    <div style={{ fontSize: 20, fontWeight: 'bold', color: '#1b5e20' }}>{formatCount(cashStageTotals[stage])}</div>
                  </button>
                ))}
              </div>

              {showCashReceipts && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {cashPendingReceipts > 0 && (
                      <div onClick={() => handleStageClick(6, true)} style={{ flex: '1 1 100px', padding: '12px 10px', border: '1px solid #81c784', borderRadius: 6, background: '#f1f8f4', cursor: 'pointer', textAlign: 'center' }}>
                        <div style={{ fontSize: 14, fontWeight: 'bold', color: '#1b5e20', marginBottom: 4 }}>Pending Receipts</div>
                        <div style={{ fontSize: 18, fontWeight: 'bold', color: '#1b5e20' }}>{formatCount(cashPendingReceipts)}</div>
                      </div>
                    )}
                    {cashUploadedReceipts > 0 && (
                      <div onClick={() => handleStageClick(7, true)} style={{ flex: '1 1 100px', padding: '12px 10px', border: '1px solid #81c784', borderRadius: 6, background: '#f1f8f4', cursor: 'pointer', textAlign: 'center' }}>
                        <div style={{ fontSize: 14, fontWeight: 'bold', color: '#1b5e20', marginBottom: 4 }}>Uploaded Receipts</div>
                        <div style={{ fontSize: 18, fontWeight: 'bold', color: '#1b5e20' }}>{formatCount(cashUploadedReceipts)}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: 22, textAlign: 'right' }}>
            <button onClick={handleClose} className="btn btn-sm btn-secondary">Acknowledge</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayablesNotification;
